import { Link } from "../lib/route";
import { ArrowRight, Award, HeartHandshake, Sprout } from "lucide-react";
import Hero from "../components/Hero";
import CTABanner from "../components/CTABanner";
import { IMAGES, SITE, COPY } from "../lib/_data";

const DEFAULT_VALUES = [
  {
    icon: HeartHandshake,
    title: "Neighbors, not customers",
    body:
      "We live where we work. Every job is a chance to earn a lifelong customer — and often, a friend.",
  },
  {
    icon: Award,
    title: "Craft over speed",
    body:
      "We'd rather finish a job right on Tuesday than half-right on Monday. Every detail gets the same care.",
  },
  {
    icon: Sprout,
    title: "Built to last",
    body:
      "Locally owned since 2011, growing only by word of mouth. No corporate parent, no rotating techs.",
  },
];
const ICON_POOL = { HeartHandshake, Award, Sprout };

export default function About() {
  const hero = COPY.about.hero;
  const story = COPY.about.story;
  const values = COPY.about.values;
  const trust = COPY.about.trust;
  const finalCta = COPY.about.finalCta;
  const storyParas = (story.paragraphs && story.paragraphs.length ? story.paragraphs : [
    `${SITE.brandFull} started in 2011 in a two-car garage with one beat-up truck and a stubborn belief: that a local home services company could actually be honest, punctual, and craftsman-quality — all three, all the time.`,
    "Fifteen years later, we're still owner-operated. Our technicians are W-2 employees (never subcontractors), background-checked, trained in-house, and paid a living wage. That's how you get consistency you can feel across every service call.",
    "We don't run ads. We grow because the family down the street tells the family two doors down. And we protect that trust like it's the only thing we have — because it is.",
  ]);
  const valueItems = (values.items && values.items.length ? values.items.map(v => ({
    icon: ICON_POOL[v.icon] || HeartHandshake,
    title: v.title,
    body: v.body,
  })) : DEFAULT_VALUES);
  const trustStats = (trust.stats && trust.stats.length ? trust.stats : [
    { k: "15+", v: "Years serving locals" },
    { k: "4.9★", v: "Average review" },
    { k: "300+", v: "Homes helped" },
  ]);
  return (
    <div data-testid="page-about">
      <Hero
        image={IMAGES.aboutHero}
        eyebrow={hero.eyebrow || "Locally owned since 2011"}
        title={hero.title || "A local team that treats your home like our own."}
        subtitle={hero.subtitle || "Founded by a father-and-son duo who got tired of watching neighbors get overcharged and under-served. We built the company we wanted to hire."}
        primaryCta={{ label: hero.primaryCtaLabel || "Book a Consultation", to: hero.primaryCtaTo || "/consultations" }}
        secondaryCta={{ label: hero.secondaryCtaLabel || "See Our Work", to: hero.secondaryCtaTo || "/services" }}
      />

      {/* Story */}
      <section className="bg-[#f9f6f0] py-20 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="about-story-eyebrow">
              {story.eyebrow || "Our story"}
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="about-story-title">
              {story.title || "Small enough to care. Experienced enough to trust."}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#2c2a29] leading-relaxed text-lg" data-testid="about-story-text">
            {storyParas.map((p, i) => (<p key={i}>{p}</p>))}
          </div>
        </div>
      </section>

      {/* Mission / values */}
      <section
        data-testid="about-values"
        className="bg-[#fffdf9] py-20 md:py-32 border-y border-[#e5e0d8]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="about-values-eyebrow">
              {values.eyebrow || "Our mission"}
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="about-values-title">
              {values.title || "Do what we said, when we said, for the price we said."}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="about-values-items">
            {valueItems.map((v) => (
              <div
                key={v.title}
                className="bg-[#f9f6f0] rounded-2xl p-8 border border-[#e5e0d8]"
              >
                <span className="w-12 h-12 rounded-xl bg-[#c85a3c]/10 text-[#c85a3c] flex items-center justify-center">
                  <v.icon className="w-6 h-6" strokeWidth={2.4} />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-[#2c2a29]">
                  {v.title}
                </h3>
                <p className="mt-3 text-[#5c5855] leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why locals trust us */}
      <section className="bg-[#f9f6f0] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden aspect-[4/3] border border-[#e5e0d8] bg-gradient-to-br from-[#2c2a29] via-[#4a3d33] to-[#c85a3c]" data-testid="about-trust-image">
            {IMAGES.whyChooseUs && (
              <img
                src={IMAGES.whyChooseUs}
                alt="Team at work"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
          </div>
          <div>
            <span className="text-xs tracking-[0.24em] uppercase font-bold text-[#c85a3c]" data-testid="about-trust-eyebrow">
              {trust.eyebrow || "Why locals trust us"}
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-black text-[#2c2a29] leading-[1.05] mt-4" data-testid="about-trust-title">
              {trust.title || "You know our name. Your neighbors know our work."}
            </h2>
            <div className="mt-8 grid grid-cols-3 gap-4" data-testid="about-stats">
              {trustStats.map((s) => (
                <div
                  key={s.k}
                  className="bg-[#fffdf9] rounded-xl p-5 border border-[#e5e0d8] text-center"
                >
                  <div className="font-display text-3xl md:text-4xl font-black text-[#c85a3c]">
                    {s.k}
                  </div>
                  <div className="text-xs text-[#5c5855] mt-1 font-medium">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
            <Link
              to={story.ctaTo || "/consultations"}
              data-testid="about-book-consult"
              className="mt-10 inline-flex items-center gap-2 bg-[#c85a3c] hover:bg-[#a64b32] text-[#fffdf9] font-bold px-7 py-4 rounded-full transition-all hover:-translate-y-0.5"
            >
              {story.ctaLabel || "Book a Consultation"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTABanner
        image={IMAGES.finalCta}
        eyebrow={finalCta.eyebrow || "Ready when you are"}
        title={finalCta.title || "Come see the difference a local team makes."}
        subtitle={finalCta.subtitle || "One conversation is usually all it takes. Book a free consultation today."}
        primary={{ label: finalCta.primaryLabel || "Book a Consultation", to: finalCta.primaryTo || "/consultations" }}
        secondary={{ label: finalCta.secondaryLabel || "Request a Quote", to: finalCta.secondaryTo || "/contact" }}
      />
    </div>
  );
}
